import { createFileRoute, Link } from "@tanstack/react-router";
import { UpgradePrompt } from "@/components/site/UpgradePrompt";
import { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { TodayActivation } from "@/components/site/TodayActivation";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import { SITE_URL } from "@/lib/site";
import {
  ACTIVATIONS_ID,
  PORTRAIT_ALT,
  PORTRAIT_URL,
  breadcrumbs,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  seoMeta,
} from "@/lib/schema";
import {
  ACTIVATION_SERIES,
  SERIES_BY_SLUG,
  formatPublishDate,
  type ActivationSeries,
} from "@/lib/activation-series";

const TITLE = "Daily Activations by Buddha KA | KAfrequency Spoken Practice";
const DESCRIPTION =
  "Daily activations from Buddha KA and KAfrequency — short spoken transmissions for attention, breath, energetic ownership, and the ordering of the interior.";

export const Route = createFileRoute("/activations/")({
  component: ActivationsArchive,
  head: () => ({
    ...seoMeta({
      title: TITLE,
      description: DESCRIPTION,
      path: "/activations",
      image: PORTRAIT_URL,
      imageAlt: PORTRAIT_ALT,
    }),
    scripts: [
      // CollectionPage only — individual activation rows are published
      // continuously and are described on their own detail routes.
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: ACTIVATIONS_ID,
          url: `${SITE_URL}/activations`,
          name: "Daily Activations",
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: { creator: personRef(), publisher: orgRef() },
        }),
      ),
      ldScript(breadcrumbs([["Daily Activations", "/activations"]])),
    ],
  }),
});


type A = Database["public"]["Tables"]["activations"]["Row"];

type Sort = "newest" | "oldest" | "most-shared";

function ActivationsArchive() {
  const [rows, setRows] = useState<A[]>([]);
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState<ActivationSeries | "all">("all");
  const [year, setYear] = useState<number | "all">("all");
  const [month, setMonth] = useState<number | "all">("all");
  const [sort, setSort] = useState<Sort>("newest");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("activations")
        .select("id, slug, title, subtitle, intention, series, slot, publish_at, activation_number, hero_image_url, share_count, duration")
        .order("publish_at", { ascending: false, nullsFirst: false });
      if (cancelled) return;
      setRows((data ?? []) as A[]);
      setLoading(false);
    })();
    (async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;
      const { data } = await supabase
        .from("user_favorites")
        .select("slug")
        .eq("kind", "activation")
        .eq("user_id", user.user.id);
      if (cancelled) return;
      setFavorites(new Set((data ?? []).map((r) => r.slug)));
    })();
    return () => { cancelled = true; };
  }, []);

  const years = useMemo(() => {
    const set = new Set<number>();
    for (const r of rows) if (r.publish_at) set.add(new Date(r.publish_at).getFullYear());
    return Array.from(set).sort((a, b) => b - a);
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows.filter((r) => {
      if (series !== "all" && r.series !== series) return false;
      if (r.publish_at) {
        const d = new Date(r.publish_at);
        if (year !== "all" && d.getFullYear() !== year) return false;
        if (month !== "all" && d.getMonth() !== month) return false;
      }
      if (favoritesOnly && !favorites.has(r.slug)) return false;
      return true;
    });
    if (sort === "newest") out = out.slice().sort((a, b) => (b.publish_at ?? "").localeCompare(a.publish_at ?? ""));
    else if (sort === "oldest") out = out.slice().sort((a, b) => (a.publish_at ?? "").localeCompare(b.publish_at ?? ""));
    else if (sort === "most-shared") out = out.slice().sort((a, b) => (b.share_count ?? 0) - (a.share_count ?? 0));
    return out;
  }, [rows, series, year, month, favoritesOnly, favorites, sort]);

  return (
    <>
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-40" aria-hidden />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">KAfrequency</p>
          <h1 className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl">Daily Activations</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            A living archive. Every day at 5:00 AM, 11:00 AM, 5:00 PM, and 7:47 PM a new transmission enters the field.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/activations/calendar" className="rounded-full border-gold border px-5 py-2.5 text-sm hover:bg-gold hover:text-obsidian">
              Open the calendar
            </Link>
            <Link to="/activations" hash="series" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold">
              Explore the 10 series
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <TodayActivation />

        <div id="series" className="mb-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {ACTIVATION_SERIES.map((s) => (
            <Link
              key={s.slug}
              to="/activations/series/$series"
              params={{ series: s.slug }}
              className="group rounded-xl border hairline bg-charcoal p-4 transition hover:border-gold"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Series {s.number}</p>
              <h3 className="mt-2 font-serif text-lg group-hover:text-gold-luminous">{s.label}</h3>
              <p className="mt-2 text-xs text-muted-foreground">{s.tagline}</p>
            </Link>
          ))}
        </div>

        <div className="mb-8 flex flex-wrap items-center gap-3 border-t hairline pt-6">
          <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Filter</span>
          <select aria-label="Filter by series" value={series} onChange={(e) => setSeries(e.target.value as ActivationSeries | "all")} className="rounded-full border hairline bg-transparent px-3 py-1.5 text-xs">
            <option value="all">All series</option>
            {ACTIVATION_SERIES.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
          </select>
          <select aria-label="Filter by year" value={year} onChange={(e) => setYear(e.target.value === "all" ? "all" : Number(e.target.value))} className="rounded-full border hairline bg-transparent px-3 py-1.5 text-xs">
            <option value="all">Any year</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select aria-label="Filter by month" value={month} onChange={(e) => setMonth(e.target.value === "all" ? "all" : Number(e.target.value))} className="rounded-full border hairline bg-transparent px-3 py-1.5 text-xs">
            <option value="all">Any month</option>
            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select aria-label="Sort activations" value={sort} onChange={(e) => setSort(e.target.value as Sort)} className="rounded-full border hairline bg-transparent px-3 py-1.5 text-xs">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="most-shared">Most shared</option>
          </select>
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={favoritesOnly} onChange={(e) => setFavoritesOnly(e.target.checked)} />
            Favorites
          </label>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading transmissions…</p>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border hairline bg-charcoal p-12 text-center">
            <p className="font-serif text-2xl text-gold-luminous">No activations match this view.</p>
            <p className="mt-2 text-sm text-muted-foreground">Clear a filter or wait for the next transmission.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a) => (
              <Link
                key={a.id}
                to="/activations/$slug"
                params={{ slug: a.slug }}
                className="group flex flex-col overflow-hidden rounded-xl border hairline bg-charcoal transition-all hover:border-gold hover:shadow-gold"
              >
                <ReleaseCover coverUrl={a.hero_image_url} label={a.title} aspect="square" alt={a.title} />
                <div className="flex flex-1 flex-col p-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-gold">
                    {a.activation_number ? `№ ${a.activation_number}` : "Draft"}
                    {a.series ? ` · ${SERIES_BY_SLUG[a.series as ActivationSeries]?.label}` : ""}
                  </p>
                  <h2 className="mt-3 font-serif text-2xl group-hover:text-gold-luminous">{a.title}</h2>
                  {a.subtitle && <p className="mt-1 text-sm text-muted-foreground">{a.subtitle}</p>}
                  <p className="mt-3 flex-1 text-sm text-muted-foreground">{a.intention}</p>
                  <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground">
                    {a.duration && <span className="inline-flex items-center gap-1"><Clock size={12} /> {a.duration}</span>}
                    {a.duration && a.publish_at && <span className="text-gold">·</span>}
                    {a.publish_at && <span>{formatPublishDate(a.publish_at)}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <UpgradePrompt context="activations" />
      </div>
    </>
  );
}
